<script lang="ts" setup>
import type { UploadFile, UploadInstance, UploadRawFile } from 'element-plus'
import { ElButton, ElFormItem, ElIcon, ElMessage, ElNotification, ElUpload, genFileId } from 'element-plus'
import { computed, getCurrentInstance, onBeforeUnmount, ref, useSlots, watch } from 'vue'
import { Cropper } from 'vue-advanced-cropper'

import 'vue-advanced-cropper/dist/style.css'
import { default as DeleteIcon, default as LoadingIcon } from '../../assets/icons/delete.svg?component'
import ImageIcon from '../../assets/icons/image.svg?component'
import UploadIcon from '../../assets/icons/upload.svg?component'
import type { ProcessableImage } from '../../types/common.types'
import ButtonComponent from '../atoms/ButtonComponent.vue'
import FlexComponent from '../atoms/FlexComponent.vue'
import ImageComponent from '../atoms/ImageComponent.vue'
import TypographyComponent from '../atoms/TypographyComponent.vue'
import DialogModal from '../molecules/DialogModal/DialogModal.vue'

const mimeTypeLabels: Record<string, string> = {
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'video/*': 'video',
}

const joinLabels = (labels: string[]) => {
  if (labels.length <= 1) {
    return labels[0] ?? ''
  }

  if (labels.length === 2) {
    return `${labels[0]} or ${labels[1]}`
  }

  return `${labels.slice(0, -1).join(', ')} or ${labels.at(-1)}`
}

const formatValidTypes = (validTypes: string[]) => {
  const labels = [...new Set(validTypes.map((type) => mimeTypeLabels[type] ?? type.split('/').at(-1)?.toUpperCase() ?? type))]

  return joinLabels(labels)
}

const matchesValidType = (fileType: string, validType: string) => {
  if (validType.endsWith('/*')) {
    return fileType.startsWith(validType.slice(0, -1))
  }

  return fileType === validType
}

const isImageFile = (fileType: string) => fileType.startsWith('image/')

const slots = useSlots()
const props = withDefaults(
  defineProps<{
    image?: ProcessableImage
    label?: string
    placeholder?: string
    maximumSize?: number
    validTypes?: string[]
    autoUpload?: boolean
    aspectRatio?: number
    accept?: string
    isPortraitIcon?: boolean
    isDisabled?: boolean
    showCropper?: boolean
    name?: string
    fileLabel?: string
    buttonLabel?: string
  }>(),
  {
    autoUpload: true,
    maximumSize: 10,
    validTypes: () => ['image/jpeg', 'image/png'],
    aspectRatio: 16 / 9,
    accept: 'image/jpeg, image/png',
    label: '',
    placeholder: '',
    image: undefined,
    isPortraitPreview: false,
    isDisabled: false,
    showCropper: true,
    name: 'uploadingImage',
    fileLabel: 'image',
    buttonLabel: 'Upload image',
  },
)
const instance = getCurrentInstance()
const emits = defineEmits(['crop'])
const imageThumbnail = defineModel<UploadRawFile | Blob | undefined>('imageThumbnail')

const cropperRef = ref<InstanceType<typeof Cropper>>()
const fileList = ref<UploadFile[]>([])
const uploadRef = ref<UploadInstance>()
const error = ref<string>('')
const previewUrl = ref<string | null>(null)

const isLoading = ref(false)
const isCropperVisible = ref(false)

const invalidTypeMessage = computed(() => `The ${props.fileLabel} must be in ${formatValidTypes(props.validTypes)} format`)

const sizeExceededMessage = computed(() => `The size of the ${props.fileLabel} can not exceed ${props.maximumSize}mb`)

const isVideoThumbnail = computed(() => imageThumbnail.value?.type?.startsWith('video/') ?? false)

watch(
  () => imageThumbnail.value,
  (file) => {
    if (previewUrl.value) {
      URL.revokeObjectURL(previewUrl.value)
      previewUrl.value = null
    }

    if (file) {
      previewUrl.value = URL.createObjectURL(file)
    }
  },
  { immediate: true },
)

watch(
  () => props.showCropper,
  (showCropper) => {
    if (!showCropper) {
      isCropperVisible.value = false
    }
  },
)

onBeforeUnmount(() => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
  }
})

const handleImageError = (e: Error) => {
  ElNotification.error({
    message: 'message' in e ? e.message : 'Oops! Something went wrong.',
    duration: 0,
  })
  isLoading.value = false
}

const handleChange = (file: UploadFile) => {
  const rawFile = file.raw
  error.value = ''

  if (rawFile) {
    if (!props.validTypes.some((validType) => matchesValidType(rawFile.type, validType))) {
      error.value = invalidTypeMessage.value
      ElMessage({
        message: invalidTypeMessage.value,
        type: 'error',
        offset: 88,
        duration: 5000,
      })
      uploadRef.value?.clearFiles()
      return false
    } else if (rawFile.size / 1024 / 1024 > props.maximumSize) {
      error.value = sizeExceededMessage.value
      ElMessage({
        message: sizeExceededMessage.value,
        type: 'error',
        offset: 88,
        duration: 5000,
      })
      uploadRef.value?.clearFiles()
      return false
    }

    imageThumbnail.value = rawFile
    isCropperVisible.value = props.showCropper && isImageFile(rawFile.type)
  }
}

const handleExceed = (files: File[]) => {
  const nextFile = files[0] as UploadRawFile | undefined

  if (!nextFile) {
    return
  }

  uploadRef.value?.clearFiles()
  nextFile.uid = genFileId()
  uploadRef.value?.handleStart(nextFile)

  if (props.autoUpload) {
    uploadRef.value?.submit()
  }
}

const beforeImageUpload = () => {
  isLoading.value = true
  return true
}

const handleSuccess = () => {
  isLoading.value = false
}

const removeThumbnail = (e: MouseEvent) => {
  e.stopPropagation()
  uploadRef.value?.clearFiles()
  isLoading.value = false
  imageThumbnail.value = undefined
}

const closeCropper = () => {
  isCropperVisible.value = false
  imageThumbnail.value = undefined
  uploadRef.value?.clearFiles()
}

const handleCrop = () => {
  if (cropperRef.value && imageThumbnail.value && isImageFile(imageThumbnail.value.type)) {
    const { canvas } = cropperRef.value.getResult()
    if (canvas) {
      canvas.toBlob((blob) => {
        if (blob) {
          if (instance?.vnode?.props?.onCrop) {
            emits('crop', blob)
          }
          imageThumbnail.value = blob
          isCropperVisible.value = false
          uploadRef.value?.clearFiles()
        }
      }, imageThumbnail.value.type)
    }
  }
}
</script>
<template>
  <div>
    <slot v-if="slots.label" name="label" />
    <TypographyComponent v-else-if="label" variant="title-large">{{ label }}</TypographyComponent>

    <ElFormItem :prop="name" :error="error">
      <ElUpload
        ref="uploadRef"
        v-model:file-list="fileList"
        :accept="accept"
        class="w-100"
        drag
        action="#"
        :name="name"
        :limit="1"
        :show-file-list="false"
        :on-success="handleSuccess"
        :on-error="handleImageError"
        :auto-upload="autoUpload"
        :before-upload="beforeImageUpload"
        :on-change="handleChange"
        :on-exceed="handleExceed"
        :disabled="isDisabled || isLoading"
      >
        <FlexComponent :gap="16" basis="100%" class="upload-input__wrapper">
          <FlexComponent align="center" justify="center" shrink="0" :class="['upload-input__container', { 'is-portrait': isPortraitIcon }]">
            <template v-if="previewUrl">
              <video v-if="isVideoThumbnail" :src="previewUrl" class="upload-input__thumbnail" preload="metadata" muted playsinline />
              <img v-else :src="previewUrl" class="upload-input__thumbnail" alt="upload image" />
              <span class="upload-input__thumbnail-actions" @click.prevent="removeThumbnail">
                <ElIcon :size="24" class="cursor-pointer">
                  <DeleteIcon />
                </ElIcon>
              </span>
            </template>
            <template v-else-if="image">
              <ImageComponent :image="image" :width="170" :height="96" alt="upload image" class="upload-input__thumbnail" />
            </template>
            <ElIcon v-else :class="{ 'is-loading': isLoading }" :size="24">
              <LoadingIcon v-if="isLoading" />
              <ImageIcon v-else />
            </ElIcon>
          </FlexComponent>
          <FlexComponent flex="1" direction="column" justify="center" basis="100%">
            <div class="el-upload__text">
              <slot v-if="slots.placeholder" name="placeholder" />
              <TypographyComponent v-else-if="placeholder" variant="body-small" color="var(--secondary-64)">
                {{ placeholder }}
              </TypographyComponent>
            </div>
            <FlexComponent justify="space-around">
              <ButtonComponent variant="outlined" class="upload-input__upload-button">
                <template #iconLeft>
                  <ElIcon :size="18">
                    <UploadIcon />
                  </ElIcon>
                </template>
                {{ buttonLabel }}
              </ButtonComponent>
            </FlexComponent>
          </FlexComponent>
        </FlexComponent>
      </ElUpload>
    </ElFormItem>

    <DialogModal
      v-if="showCropper && isCropperVisible"
      v-model="isCropperVisible"
      width="600px"
      size="auto"
      direction="btt"
      :before-close="closeCropper"
    >
      <Cropper
        ref="cropperRef"
        class="upload-input__cropper"
        background-class="upload-input__cropper-background"
        :src="previewUrl"
        :stencil-props="{ aspectRatio }"
      />
      <template #footer>
        <FlexComponent justify="flex-end" :gap="16">
          <ElButton size="large" @click="closeCropper">Cancel</ElButton>
          <ElButton type="primary" size="large" @click="handleCrop">Crop image</ElButton>
        </FlexComponent>
      </template>
    </DialogModal>
  </div>
</template>
<style lang="scss" scoped>
$breakpoint-lg: 992px;
:deep(.el-upload-dragger) {
  padding: 16px;
  border-radius: 8px;
  --el-color-primary: var(--secondary-64);
  border: 1px solid var(--secondary-12);
  transition: border-color var(--transition) ease;

  &:hover {
    border-color: var(--secondary-80);
  }
}
.upload-input__wrapper {
  flex-wrap: wrap;
  justify-content: center;
  @media (min-width: $breakpoint-lg) {
    flex-wrap: initial;
    justify-content: initial;
  }
}
.upload-input__container {
  position: relative;
  width: 170px;
  height: 96px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px dashed var(--secondary-18);

  &.is-portrait {
    width: 80px;
    height: 140px;
  }

  &:hover .upload-input__thumbnail-actions {
    opacity: 1;
  }
}

.upload-input__thumbnail {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-input__thumbnail-actions {
  background: var(--secondary-80);
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  color: #fff;
  z-index: 2;
  opacity: 0;
  cursor: pointer;

  :deep(.el-icon path) {
    fill: #fff;
  }
}

.upload-input__cropper {
  max-height: 600px;
  margin: 24px 0;
  background: #fff;
}
:deep(.upload-input__cropper-background) {
  background: #fff;
}
.button.upload-input__upload-button {
  padding: 8px 16px;
  font-weight: 600;
}
</style>
